/**
 * Pipeline board (A7Y-5209): `/admin/cmo/crm/board`.
 *
 * Every account from `GET /admin/crm/accounts`, read live, as a card in its
 * stage column (Lead → Onboarded), with partners and paused or closed accounts
 * below. Filters narrow by segment, who the account runs through and the
 * 30-day quiet flag. The card layout and rules live in `board.ts`; each card
 * opens its account page. Replaces the ritual-redrawn artifact (A7Y-5189).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { HelmViewNav } from '../HelmViews';
import { api } from '@/lib/api';
import { isoDate } from './accountView';
import { crmErrorMessage, isCrmUnavailableError } from './crmErrors';
import { accountPagePath, splitAccountName } from './links';

import {
  ALL_CARDS,
  OWNERS,
  QUIET_DAYS,
  fallbackLabel,
  layoutBoard,
  nextLine,
  shortDate,
  triggerLine,
} from './board';
import { rankLabel } from './scoreboard';
import type { BoardCard, BoardFilters, SegmentFilter } from './board';

function crmRetry(err: unknown, failureCount: number): boolean {
  return !isCrmUnavailableError(err) && failureCount < 1;
}

const SEGMENT_CHIPS: { value: SegmentFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'mid', label: 'Mid-size' },
  { value: 'agency', label: 'Agency' },
];

const SEGMENT_LABEL: Record<BoardCard['segment'], string> = {
  enterprise: 'Enterprise',
  mid: 'Mid-size',
  agency: 'Agency',
  partner: 'Partner',
};

const SEGMENT_TONE: Record<BoardCard['segment'], string> = {
  enterprise: 'text-indigo-700 ring-indigo-300 dark:text-indigo-300 dark:ring-indigo-700',
  mid: 'text-teal-700 ring-teal-300 dark:text-teal-300 dark:ring-teal-700',
  agency: 'text-fuchsia-700 ring-fuchsia-300 dark:text-fuchsia-300 dark:ring-fuchsia-700',
  partner: 'text-zinc-600 ring-zinc-300 dark:text-zinc-300 dark:ring-zinc-600',
};

const TAG =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] leading-4 ring-1 ring-inset';

export function CrmBoardPage({ today = isoDate(new Date()) }: { today?: string }) {
  const accounts = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: api.getCrmAccounts,
    retry: crmRetry,
  });
  const [filters, setFilters] = useState<BoardFilters>(ALL_CARDS);

  let body: React.ReactNode;
  if (isCrmUnavailableError(accounts.error)) {
    body = (
      <p data-testid="crm-unavailable" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record is available on production only.
      </p>
    );
  } else if (accounts.error != null) {
    body = (
      <p
        role="alert"
        data-testid="crm-error"
        className="px-2 text-sm text-red-600 dark:text-red-400"
      >
        Could not load the board: {crmErrorMessage(accounts.error)}
      </p>
    );
  } else if (accounts.data === undefined) {
    body = (
      <p data-testid="crm-loading" className="px-2 text-sm text-zinc-500">
        Loading…
      </p>
    );
  } else if (!accounts.data.imported) {
    body = (
      <p data-testid="crm-not-imported" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record has not been imported yet
      </p>
    );
  } else {
    body = (
      <Board
        items={accounts.data.accounts}
        filters={filters}
        onFilters={setFilters}
        today={today}
      />
    );
  }

  return (
    <div className="pb-12">
      <HelmViewNav />
      <PageHeader
        title="Pipeline board"
        subtitle="Every account by stage, live from HELM's record"
      />
      {body}
    </div>
  );
}

function Board({
  items,
  filters,
  onFilters,
  today,
}: {
  items: Parameters<typeof layoutBoard>[0];
  filters: BoardFilters;
  onFilters: (next: BoardFilters) => void;
  today: string;
}) {
  const board = layoutBoard(items, filters, today);
  const { totals } = board;
  return (
    <div className="space-y-6 px-2">
      <p
        data-testid="crm-board-summary"
        className="max-w-3xl text-sm text-zinc-600 dark:text-zinc-400"
      >
        <strong className="font-semibold tabular-nums text-zinc-900 dark:text-white">
          {totals.customersInPlay}
        </strong>{' '}
        customers and prospects in play,{' '}
        <strong className="font-semibold tabular-nums text-zinc-900 dark:text-white">
          {totals.quietCustomers}
        </strong>{' '}
        of them quiet for {QUIET_DAYS} days or more;{' '}
        <strong className="font-semibold tabular-nums text-zinc-900 dark:text-white">
          {totals.partnersInPlay}
        </strong>{' '}
        partners;{' '}
        <strong className="font-semibold tabular-nums text-zinc-900 dark:text-white">
          {totals.parked}
        </strong>{' '}
        paused or closed.
      </p>

      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-3"
        role="group"
        aria-label="Board filters"
      >
        <ChipGroup label="Segment">
          {SEGMENT_CHIPS.map((chip) => (
            <Chip
              key={chip.value}
              pressed={filters.segment === chip.value}
              onClick={() => onFilters({ ...filters, segment: chip.value })}
            >
              {chip.label}
            </Chip>
          ))}
        </ChipGroup>
        <ChipGroup label="Runs through">
          <Chip
            pressed={filters.owner === 'all'}
            onClick={() => onFilters({ ...filters, owner: 'all' })}
          >
            Everyone
          </Chip>
          {OWNERS.map((owner) => (
            <Chip
              key={owner}
              pressed={filters.owner === owner}
              onClick={() => onFilters({ ...filters, owner })}
            >
              {owner}
            </Chip>
          ))}
        </ChipGroup>
        <Chip
          pressed={filters.quietOnly}
          onClick={() => onFilters({ ...filters, quietOnly: !filters.quietOnly })}
        >
          Quiet {QUIET_DAYS}+ days only
        </Chip>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="grid min-w-[1180px] grid-cols-7 items-start gap-3">
          {board.columns.map((column) => (
            <section
              key={column.stage}
              aria-label={column.stage}
              data-testid={`crm-board-column-${column.stage}`}
              className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-2 dark:bg-zinc-800/60"
            >
              <h2 className="flex items-baseline justify-between px-1 text-sm font-semibold capitalize text-zinc-900 dark:text-white">
                {column.stage}
                <span className="text-xs font-normal tabular-nums text-zinc-500">
                  {column.cards.length}
                </span>
              </h2>
              {column.cards.length === 0 ? (
                <p className="px-1 text-xs text-zinc-500">None with these filters</p>
              ) : (
                column.cards.map((card) => <Card key={card.id} card={card} today={today} />)
              )}
            </section>
          ))}
        </div>
      </div>

      <CardSection
        title="Partners"
        testId="crm-board-partners"
        cards={board.partners}
        today={today}
        showStage
      />
      {filters.quietOnly ? null : (
        <CardSection
          title="Paused and closed"
          testId="crm-board-parked"
          cards={board.parked}
          today={today}
        />
      )}

      <p className="max-w-3xl text-xs text-zinc-500">
        &ldquo;Runs through&rdquo; comes from a fixed list of Enric&rsquo;s and Thomas&rsquo;s
        accounts, because HELM&rsquo;s record has no owner field. Stage notes, next actions and
        history stay on each account&rsquo;s page.
      </p>
    </div>
  );
}

function CardSection({
  title,
  testId,
  cards,
  today,
  showStage = false,
}: {
  title: string;
  testId: string;
  cards: BoardCard[];
  today: string;
  showStage?: boolean;
}) {
  return (
    <section aria-label={title} data-testid={testId} className="space-y-2">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
        {title}
        <span className="ml-2 text-xs font-normal tabular-nums text-zinc-500">{cards.length}</span>
      </h2>
      {cards.length === 0 ? (
        <p className="text-xs text-zinc-500">None with these filters</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
          {cards.map((card) => (
            <Card key={card.id} card={card} today={today} showStage={showStage} />
          ))}
        </div>
      )}
    </section>
  );
}

function Card({
  card,
  today,
  showStage = false,
}: {
  card: BoardCard;
  today: string;
  showStage?: boolean;
}) {
  const next = nextLine(card, today);
  const trigger = triggerLine(card, today);
  const parked = card.stage === 'dormant' || card.stage === 'closed';
  return (
    <Link
      to={accountPagePath(card.id)}
      data-testid="crm-board-card"
      data-account-id={card.id}
      className={`block space-y-1.5 rounded-md border bg-white p-2.5 text-left shadow-sm transition hover:border-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:bg-zinc-900 ${
        card.quiet
          ? 'border-amber-400 dark:border-amber-600'
          : 'border-zinc-200 dark:border-zinc-700'
      }`}
    >
      <div className="text-[13px] font-semibold leading-snug text-zinc-900 [overflow-wrap:anywhere] dark:text-white">
        {splitAccountName(card.name).name}
      </div>
      {card.contacts.length > 0 ? (
        <div
          data-testid="crm-board-contacts"
          className="line-clamp-2 text-xs text-zinc-500 dark:text-zinc-400"
        >
          {card.contacts.join(' · ')}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {showStage ? (
          <span
            className={`${TAG} bg-zinc-900 capitalize text-white ring-zinc-900 dark:bg-white dark:text-zinc-900`}
          >
            {card.stage}
          </span>
        ) : null}
        {card.rank ? (
          <span
            data-testid="crm-board-rank"
            className={`${TAG} bg-amber-50 font-semibold text-amber-900 ring-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-900`}
          >
            {rankLabel(card.rank)}
          </span>
        ) : null}
        <span className={`${TAG} ${SEGMENT_TONE[card.segment]}`}>
          {SEGMENT_LABEL[card.segment]}
        </span>
        {card.owner !== 'Yevhen' ? (
          <span
            data-testid="crm-board-owner"
            className={`${TAG} bg-blue-50 text-blue-800 ring-blue-100 dark:bg-blue-950 dark:text-blue-200 dark:ring-blue-900`}
          >
            {card.owner}
          </span>
        ) : null}
        {card.tag || card.stage === 'closed' ? (
          <span className={`${TAG} text-zinc-500 ring-zinc-300 dark:ring-zinc-600`}>
            {card.tag ?? 'Closed'}
          </span>
        ) : null}
      </div>
      <div className="space-y-0.5 text-xs tabular-nums text-zinc-600 dark:text-zinc-400">
        <div>
          {card.lastTouch ? (
            <>
              Last touch {shortDate(card.lastTouch, today)}
              {card.quiet ? (
                <span
                  data-testid="crm-board-quiet"
                  className="ml-1.5 rounded bg-amber-50 px-1.5 font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                >
                  quiet {card.daysSinceTouch} days
                </span>
              ) : null}
            </>
          ) : (
            'No last touch recorded'
          )}
        </div>
        {parked ? null : (
          <div data-testid="crm-board-next">
            {next.kind === 'trigger'
              ? `Next: waits on a trigger${next.date ? ` · ${fallbackLabel(next.date, today)}` : ''}`
              : null}
            {next.kind === 'none' ? 'No next date' : null}
            {next.kind === 'today' ? (
              <>
                Next{' '}
                <span className="rounded bg-blue-50 px-1.5 font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                  today
                </span>
              </>
            ) : null}
            {next.kind === 'overdue' ? (
              <>
                Next{' '}
                <span className="rounded bg-red-50 px-1.5 font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                  was due {shortDate(next.date, today)}
                </span>
              </>
            ) : null}
            {next.kind === 'upcoming' ? `Next ${shortDate(next.date, today)}` : null}
          </div>
        )}
        {parked || trigger === null ? null : (
          <div data-testid="crm-board-trigger">
            Also waits on a trigger
            {trigger.fallback ? ` · ${fallbackLabel(trigger.fallback, today)}` : ''}
          </div>
        )}
      </div>
    </Link>
  );
}

function ChipGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      {children}
    </div>
  );
}

function Chip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs ring-1 ring-inset transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
        pressed
          ? 'bg-zinc-900 text-white ring-zinc-900 dark:bg-white dark:text-zinc-900 dark:ring-white'
          : 'bg-white text-zinc-700 ring-zinc-300 hover:ring-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:ring-zinc-700'
      }`}
    >
      {children}
    </button>
  );
}
